import React from 'react';

/**
 * UserName — a user's display name on a `historical` surface (order lists and
 * the dashboards' requestor tables), with a deactivated colleague signalled by
 * the COLOUR OF THE NAME ITSELF plus a tooltip, rather than by a pill beside it.
 *
 * Policy: docs/USER_STATUS.md ("Client rendering").
 *
 *   <UserName name={order.requested_by} isActive={order.is_active} />
 *
 * The server exposes `is_active` (1/0 on SQLite, true/false on Postgres) on
 * every historical surface, so both shapes are coerced below. Nothing about
 * this module is a permission decision — it renders a flag the server already
 * sent, and `is_active` stays on the payload regardless of what is drawn here.
 *
 * ── ⚠️ CONTRACT CHANGE 2026-08-07: THE GREY `inactive` PILL IS GONE ──────
 * `InactiveBadge`, the default export of this file, rendered a small grey
 * `inactive` pill next to the name. It was DELETED, not merely unused, on the
 * product owner's decision — twice over:
 *
 *   1. *"no need to show the user status 'inactive' for example next to users
 *      in orders and to approve etc"* — the pill is visual noise on a list.
 *   2. *"why not turn the users name yellow or red with a tooltip?"* — asked
 *      when it was put to him that removing it outright costs real information
 *      (you could no longer tell an order was raised by someone who has left,
 *      which is what you want to know before chasing them).
 *
 * So the INFORMATION IS RETAINED and only the second visual element is
 * removed. Do not "restore" the pill as a regression: it is not an oversight,
 * and the component that drew it no longer exists precisely so that nobody
 * finds it, assumes it is current, and puts it back. Reinstating the old
 * behaviour means writing a new component and changing the policy document.
 *
 * ── Why amber and not red ────────────────────────────────────────────
 * Red is this codebase's ERROR/DANGER colour (`BTN` danger is red-600,
 * `constants/buttons.js`). A colleague who has left is a CAUTION, not a fault
 * — nothing has gone wrong and there is nothing to fix — and amber is already
 * the house colour for "look at this" (the dirty-guard pulse, the warning
 * button intent). Red would also collide with the rejected/cancelled status
 * badges that sit a few columns away on the very same row.
 *
 * ── Why these two shades and not any other amber ─────────────────────
 * `docs/USER_STATUS.md` sets a readability floor for exactly this kind of
 * "quieter" name treatment on the people directory, and quotes its greys at
 * ~4.8:1 on white and ~6.4:1 on `gray-800`. These clear it, measured against
 * every background the name is actually drawn on (WCAG 2.x relative
 * luminance):
 *
 *   text-amber-700 (#b45309) on white                 5.02:1
 *   text-amber-700 (#b45309) on gray-50  (row hover)  4.86:1
 *   text-amber-700 (#b45309) on gray-100 (expanded)   4.56:1
 *   dark:text-amber-400 (#fbbf24) on gray-800         8.79:1
 *   dark:text-amber-400 (#fbbf24) on slate-700        6.20:1
 *
 * The worst case is the expanded-row tint in light mode at 4.56:1, still over
 * the 4.5:1 AA floor for body text. **Do not reach for `amber-500` in light
 * mode** — it is ~2.1:1 on white and fails outright. And do NOT express this
 * as an `opacity-*` utility: that is the dirty-guard idiom in this codebase
 * (`opacity-40 pointer-events-none`) and means something else entirely.
 *
 * ── Colour is NOT the only signal ────────────────────────────────────
 * Colour alone fails a colour-blind reader and anyone who never hovers, so the
 * meaning is carried twice more, both from the SAME string so the three can
 * never drift:
 *
 *   - `title` — the hover tooltip the product owner asked for. It sits on the
 *     name span itself, which is the element the pointer is actually over.
 *   - a visually-hidden `sr-only` copy — because `title` on a generic `<span>`
 *     (no ARIA role, no widget semantics) is announced inconsistently across
 *     screen readers, so it cannot be the only non-visual channel. `sr-only`
 *     is announced everywhere and is what makes this genuinely accessible
 *     rather than nominally so.
 *
 * 🪤 That hidden copy IS in the DOM, so it is in `textContent`. A test doing
 * `getByText('Alice Smith')` against an INACTIVE user will not match the outer
 * span (whose text is the name plus the sentence); match the name node or use
 * a substring matcher. Active users are unaffected — they render a bare span
 * with nothing added.
 */

/** The one wording, used by the tooltip AND the screen-reader copy. */
export const INACTIVE_NAME_TITLE = 'This user has been deactivated';

/**
 * The amber pair. Exported so the tests assert the same string the component
 * renders rather than a transcription of it — a transcribed class name is a
 * second source of truth that goes stale silently.
 */
export const INACTIVE_NAME_CLASS = 'text-amber-700 dark:text-amber-400';

export function UserName({ name, isActive, className = '' }) {
  // The server's CASE-WHEN-active flag is 1/0 on SQLite and true/false on
  // Postgres, so both shapes are tested explicitly. A MISSING flag
  // (`undefined`) is deliberately NOT treated as inactive: a payload that has
  // not been taught to send `is_active` must render an ordinary name, never
  // mark every person on the page as having left.
  const inactive = isActive === false || isActive === 0;

  if (!inactive) {
    return <span className={className}>{name}</span>;
  }

  return (
    <span
      className={`${INACTIVE_NAME_CLASS} ${className}`.trim()}
      title={INACTIVE_NAME_TITLE}
      data-user-inactive="true"
    >
      {name}
      {/* Not `aria-label` on the span above: that would REPLACE the name for
          assistive technology instead of adding to it, so the reader would
          hear the status and never learn who raised the order. */}
      <span className="sr-only"> {INACTIVE_NAME_TITLE}</span>
    </span>
  );
}

/**
 * ── No default export, deliberately ──────────────────────────────────
 * This module's default export USED to be `InactiveBadge`. Re-pointing the
 * default at `UserName` would mean a stale
 * `import InactiveBadge from './InactiveBadge'` kept compiling and silently
 * rendered a name component instead of a pill. With no default at all it is an
 * immediate, obvious error, which is the outcome you want from code written
 * against a contract that no longer exists.
 *
 * ── Why the FILE is still called InactiveBadge.js ────────────────────
 * The badge it is named after is gone, so the name is admittedly stale. It is
 * kept because the rename is not free and buys little: it would touch the four
 * importers, and — landing in the same commit as a near-total rewrite of the
 * file — would likely defeat git's rename detection and cost the blame trail
 * for `UserName`, which is the part that actually survived. This block is here
 * so the filename cannot mislead anyone who opens it. Renaming to
 * `UserName.js` later is a clean, mechanical change on its own.
 */
